import React from "react";
import { useStaticQuery, graphql } from "gatsby";
import clsx from "clsx";
import { useIntl } from "react-intl";

import useBalancedText from "./utils/useBalancedText";
import ContainerBleed from "@app/components/ContainerBleed";

import styles from "./PostCTA.module.css";

// We keep only banners that are valid until the next three months
const VALID_UNTIL_THRESHOLD = 2 * 30 * 24 * 60 * 60 * 1000;

export default function PostCTA({
  bannerId,
  title,
  bannerTitle: customBannerTitle,
  ...rest
}) {
  const intl = useIntl();
  const bannerNameRef = React.useRef();
  const titleRef = React.useRef();

  useBalancedText(bannerNameRef);
  useBalancedText(titleRef);

  const data = useStaticQuery(graphql`
    {
      allBannersYaml(sort: { fields: validUntil, order: ASC }) {
        edges {
          node {
            id
            name {
              ...Translations
            }
            title {
              ...Translations
            }
            validUntil
            tint {
              text
              hover
              main
            }
            padding
            alignText
            cta {
              label {
                ...Translations
              }
              url {
                ...Translations
              }
            }
            image {
              childImageSharp {
                fluid(maxWidth: 1600) {
                  src
                }
              }
            }
          }
        }
      }
    }
  `);

  const now = Date.now();

  const banners = data.allBannersYaml.edges
    .map((edge) => edge.node)
    .filter((node) => {
      if (bannerId && node.id !== bannerId) {
        return false;
      }

      if (node.validSince) {
        const validSince = Date.parse(node.validSince);

        if (validSince > now) {
          return false;
        }
      }

      if (node.validUntil) {
        const validUntil = Date.parse(node.validUntil);

        if (validUntil < now) {
          return false;
        }

        if (!node.validSince) {
          if (validUntil - now > VALID_UNTIL_THRESHOLD) {
            return false;
          }
        }
      }

      return true;
    });

  const banner = banners[0];

  if (!banner && !title) {
    return null;
  }

  const backgroundImage = banner?.image?.childImageSharp?.fluid?.src;
  const bannerName = banner?.name?.[intl.locale];
  const bannerTitle = banner?.title?.[intl.locale];
  const tintText = banner?.tint?.text;
  const tintMain = banner?.tint?.main;
  const tintHover = banner?.tint?.hover;
  const padding = banner?.padding;
  const alignText = banner?.alignText;
  const ctaLabel = banner?.cta?.label?.[intl.locale];
  const ctaUrl = banner?.cta?.url?.[intl.locale];

  return (
    <ContainerBleed
      style={{
        backgroundPosition: "50% 50%",
        backgroundSize: "cover",
        backgroundImage: backgroundImage && `url('${backgroundImage}')`,
      }}
    >
      <div
        {...rest}
        className={clsx(
          styles.container,
          banner && styles.containerBanner,
          rest.className,
        )}
        style={{
          "--text": tintText,
          "--cta-bg": tintMain,
          "--cta-bg-hover": tintHover ?? tintMain,
          "--padding": padding,
          "--align-text": alignText
        }}
      >
        <div className={styles.content}>
          {bannerName && (
            <h3 className={styles.bannerName} ref={bannerNameRef}>
              {bannerName}
            </h3>
          )}
          <h2 className={styles.title} ref={titleRef}>
            {banner ? customBannerTitle ?? bannerTitle : title}
          </h2>
          <a
            className={styles.cta}
            href={
              ctaUrl ??
              (banner
                ? "https://www.smc.it/"
                : "https://www.smc.it/contatta-smc")
            }
          >
            {ctaLabel ?? (banner ? `Scopri di più` : `Contattaci ora`)}
          </a>
        </div>
      </div>
    </ContainerBleed>
  );
}
